/**
 * B2B PDP Media Filter v6 — Shared JS asset (DOM-removal approach)
 * Hides product thumbnails/media for colors NOT in the customer's B2B price list.
 *
 * How it works:
 * 1. Extracts color abbreviation from each variant SKU (last segment, e.g. GCE from 5000-S-GCE)
 * 2. Looks up full color name from built-in chart (GCE → "Gris cendré")
 * 3. Matches that name against image alt text (e.g. "...-gris-cendre-devant")
 * 4. REMOVES blocked thumbnails + viewer items from DOM (not just display:none)
 *    → Fixes gallery arrows, zoom/lightbox, and scroll indicators
 *
 * v6 changes vs v5:
 * - DOM removal instead of display:none — fixes broken gallery arrows & scroll indicators
 * - Zoom/lightbox only shows allowed images (blocked viewer items removed from DOM)
 * - Uses Worker v2 URL + aligned cache key with b2b-fast-filter.js
 * - Reduced SparkLayer wait from 2000ms → 500ms
 * - Reinitializes thumbnail scroll indicators after filtering
 *
 * Called from each section's inline <script> via:
 *   window.B2BPDPFilter.init(config)
 *
 * Config object:
 *   thumbSelector    — CSS selector for thumbnail items (e.g. '.media-thumbs__item')
 *   viewerSelector   — CSS selector for main viewer items (e.g. '.media-viewer__item')
 *   activeSelector   — CSS selector for the currently active element (e.g. '.media-thumbs__btn.is-active')
 *   activateThumb    — function(thumbEl) to activate a thumbnail when the current one is hidden
 *   getMediaId       — function(el) to extract media ID from a thumbnail/viewer element
 */
(function() {
  'use strict';

  // ---- Aligned with b2b-fast-filter.js for shared session cache ----
  var CACHE_KEY = 'b2b_allowed_skus_v2';
  var WORKER_URL = 'https://husky-protocol.human-after-all.workers.dev/b2b/get-prices';
  var DEBUG = window.location.search.indexOf('b2b_debug=1') > -1;

  function log() {
    if (DEBUG) console.log.apply(console, ['[B2B-PDP-Filter]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ---- Color abbreviation chart (from Charte de couleurs.xlsx) ----
  // Maps SKU color codes → array of searchable names (longest/most specific first).
  // All values are pre-normalized: lowercase, no accents, no special chars.
  var COLOR_CHART = {
    'NOI': ['noir'],
    'NOV': ['noir vintage'],
    'BLA': ['blanc'],
    'BLV': ['blanc vintage'],
    'MAR': ['marine'],
    'BMF': ['bleu marin fonce'],
    'MFC': ['marin fonce cendre'],
    'DEV': ['denim vintage'],
    'GRI': ['gris sport', 'gris'],
    'GRF': ['gris fonce', 'fonce'],
    'GCH': ['gris charbon', 'charbon'],
    'GCF': ['gris cendre fonce', 'cendre fonce'],
    'GCE': ['gris cendre', 'cendre'],
    'GGC': ['gris graphite cendre', 'graphite cendre'],
    'GGS': ['gris graphite sport', 'graphite sport'],
    'GAC': ['gris athletique cendre', 'athletique cendre'],
    'ARG': ['argent froid', 'argent'],
    'TIT': ['titane'],
    'GRC': ['gris chine athletique', 'gris chine'],
    'ASP': ['asphalte'],
    'MRN': ['bourgogne', 'bordeaux', 'marron'],
    'ROU': ['rouge'],
    'ROS': ['rose'],
    'ROV': ['rouge vintage'],
    'VFO': ['vert foret'],
    'ROY': ['royal'],
    'OR':  ['orange'],
    'ORR': ['dore', 'or'],
    'SAB': ['sable'],
    'IVO': ['ivoire'],
    'TAN': ['tan'],
    'SHE': ['coquillage', 'shell'],
    'JAU': ['jaune'],
    'TEMA': ['terre marine', 'terre'],
    'NODA': ['noir dauphin'],
    'NOCY': ['noir cyan'],
    'NOAR': ['noir argent'],
    'NOCA': ['noir carbone'],
    'NOBE': ['noir bleu'],
    'GRNO': ['gris noir'],
    'NOGR': ['noir gris', 'noir graphite'],
    'GRRO': ['gris royal'],
    'MAGR': ['marine gris', 'marine graphite']
  };

  // ---- B2B Detection ----
  function isB2BCustomer() {
    return !!window.__B2B_PDP_DETECTED ||
           document.body.classList.contains('customer-b2b') ||
           !!document.querySelector('[data-customer-b2b="true"]') ||
           !!window.__B2B_CUSTOMER_EMAIL ||
           !!(window.__B2B_PRICE_LIST_SLUGS && window.__B2B_PRICE_LIST_SLUGS.length > 0);
  }

  // ---- Read allowed SKUs from sessionStorage cache (synchronous) ----
  function getCachedSkus() {
    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      var data = JSON.parse(cached);
      // 30 min cache (aligned with b2b-fast-filter.js)
      if (Date.now() - data.timestamp > 30 * 60 * 1000) return null;
      log('Using cached SKUs:', (data.skus || []).length);
      return data.skus || [];
    } catch(e) {
      return null;
    }
  }

  // ---- Fetch allowed SKUs from Worker (async, only when no cache) ----
  function fetchAllowedSkusFromWorker() {
    var requestBody = { action: 'getCustomerPriceList' };
    var directPriceLists = window.__B2B_PRICE_LIST_SLUGS || window.__B2B_PDP_PRICE_LIST_SLUGS;
    var customerGroupSlug = window.__B2B_CUSTOMER_GROUP_SLUG ||
                            (window.spark && window.spark.auth && window.spark.auth.group);

    if (directPriceLists && directPriceLists.length > 0) {
      requestBody.priceListSlugs = directPriceLists;
    } else if (customerGroupSlug) {
      requestBody.customerGroupSlug = customerGroupSlug;
    } else {
      log('No price list or customer group found');
      return Promise.resolve(null);
    }

    log('Fetching SKUs from Worker v2...');
    return fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
    .then(function(response) {
      if (!response.ok) return null;
      return response.json();
    })
    .then(function(data) {
      if (data && data.success) {
        log('Received', (data.skus || []).length, 'SKUs from Worker');
        // Cache the result (aligned key + format with b2b-fast-filter.js)
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({
            skus: data.skus || [],
            timestamp: Date.now()
          }));
        } catch(e) { /* quota exceeded — ignore */ }
        return data.skus || [];
      }
      return null;
    })
    .catch(function(err) {
      console.error('[B2B-PDP-Filter] Fetch error:', err);
      return null;
    });
  }

  // ---- Remove accents (Cendré → cendre) ----
  function removeAccents(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // ---- Normalize text for comparison ----
  function normalize(str) {
    return removeAccents(str.toLowerCase()).replace(/[-_\/]/g, ' ');
  }

  // ---- Extract color abbreviation from SKU ----
  function extractColorCode(sku) {
    if (!sku) return null;
    var parts = sku.split('-');
    if (parts.length < 2) return null;
    return parts[parts.length - 1].toUpperCase();
  }

  // ---- Remove preload-hide CSS ----
  function revealAll() {
    var s = document.getElementById('b2b-pdp-preload-hide');
    if (s) s.remove();
  }

  // ---- Reinitialize thumbnail scroll indicators after DOM changes ----
  function reinitScrollIndicators() {
    // Remove existing scroll indicators (they were calculated based on old DOM)
    var oldIndicators = document.querySelectorAll(
      '.media-thumbs-scroll-indicator, .media-thumbs-scroll-indicator-up, ' +
      '.media-thumbs-scroll-indicator-left, .media-thumbs-scroll-indicator-right'
    );
    oldIndicators.forEach(function(ind) { ind.remove(); });

    // The theme's setupThumbnailsScrollIndicator() is called via setTimeout
    // in simple-product-v2.liquid. We trigger a resize event to let it recalculate,
    // and also dispatch a custom event in case the theme listens for it.
    window.dispatchEvent(new Event('resize'));
    log('Scroll indicators reset — resize event dispatched');
  }

  // ---- Apply filter to DOM elements (shared by sync and async paths) ----
  function applyFilter(config, colorData, mediaAltMap, skus) {
    if (!skus || skus.length === 0) {
      log('No allowed SKUs, skipping filter');
      revealAll();
      return;
    }

    var allowedLower = skus.map(function(s) { return s.toLowerCase(); });
    var allowedSet = new Set(allowedLower);

    // Determine which color codes are allowed vs blocked
    var allowedCodes = new Set();
    var blockedCodes = new Set();

    Object.keys(colorData).forEach(function(code) {
      var hasAllowed = colorData[code].skus.some(function(sku) {
        var skuLower = sku.toLowerCase();
        if (allowedSet.has(skuLower)) return true;
        var prefixWithDash = skuLower + '-';
        return allowedLower.some(function(a) { return a.indexOf(prefixWithDash) === 0; });
      });
      if (hasAllowed) {
        allowedCodes.add(code);
      } else {
        blockedCodes.add(code);
      }
    });

    log('Allowed color codes:', Array.from(allowedCodes));
    log('Blocked color codes:', Array.from(blockedCodes));

    // Build sorted match entries (longest name first for correct matching)
    var allMatchEntries = [];
    Object.keys(colorData).forEach(function(code) {
      colorData[code].names.forEach(function(name) {
        allMatchEntries.push({
          code: code,
          name: name,
          allowed: allowedCodes.has(code)
        });
      });
    });
    allMatchEntries.sort(function(a, b) { return b.name.length - a.name.length; });

    log('Match entries (sorted longest first):', allMatchEntries.map(function(e) {
      return '"' + e.name + '" (' + e.code + ', ' + (e.allowed ? 'ALLOWED' : 'BLOCKED') + ')';
    }));

    // ---- Helper: check alt text against match entries ----
    function matchAlt(normalizedAlt) {
      if (!normalizedAlt) return null;
      for (var i = 0; i < allMatchEntries.length; i++) {
        if (normalizedAlt.indexOf(allMatchEntries[i].name) > -1) {
          return allMatchEntries[i];
        }
      }
      return null;
    }

    // ---- Helper: get normalized alt for an element ----
    function getAltText(el) {
      var mediaId = config.getMediaId ? config.getMediaId(el) : null;
      if (!mediaId) {
        mediaId = el.getAttribute('data-media-id');
        if (!mediaId) {
          var child = el.querySelector('[data-media-id]');
          if (child) mediaId = child.getAttribute('data-media-id');
        }
      }
      if (mediaId && mediaAltMap[mediaId]) return mediaAltMap[mediaId];
      var img = el.querySelector('img');
      if (img && img.alt) return normalize(img.alt);
      return '';
    }

    // ---- Collect blocked media IDs (for zoom/lightbox filtering) ----
    var blockedMediaIds = new Set();

    // --- Filter thumbnails (REMOVE from DOM instead of display:none) ---
    var thumbItems = document.querySelectorAll(config.thumbSelector);
    var firstVisibleThumb = null;
    var hiddenThumbCount = 0;
    var activeWasRemoved = false;

    // Check if the currently active thumb will be removed
    var currentActive = document.querySelector(config.activeSelector);

    thumbItems.forEach(function(item) {
      var alt = getAltText(item);
      if (!alt) {
        if (!firstVisibleThumb) firstVisibleThumb = item;
        item.classList.add('b2b-revealed');
        log('KEEP thumb - no alt text');
        return;
      }

      var matched = matchAlt(alt);
      if (matched) {
        if (matched.allowed) {
          if (!firstVisibleThumb) firstVisibleThumb = item;
          item.classList.add('b2b-revealed');
          log('KEEP thumb - "' + matched.name + '" (' + matched.code + ') in "' + alt + '"');
        } else {
          // Track media ID for zoom filtering
          var mediaId = item.getAttribute('data-media-id');
          if (!mediaId) {
            var child = item.querySelector('[data-media-id]');
            if (child) mediaId = child.getAttribute('data-media-id');
          }
          if (mediaId) blockedMediaIds.add(mediaId);

          // Check if this is the active thumb
          if (currentActive && item.contains(currentActive)) {
            activeWasRemoved = true;
          }

          // REMOVE from DOM (not display:none) — fixes gallery arrows
          item.remove();
          hiddenThumbCount++;
          log('REMOVE thumb - "' + matched.name + '" (' + matched.code + ') in "' + alt + '"');
        }
      } else {
        if (!firstVisibleThumb) firstVisibleThumb = item;
        item.classList.add('b2b-revealed');
        log('KEEP thumb - no color match in "' + alt + '"');
      }
    });

    // If active thumb was removed, activate the first visible one
    if (activeWasRemoved && firstVisibleThumb && config.activateThumb) {
      // Small delay to let DOM settle before clicking
      setTimeout(function() {
        config.activateThumb(firstVisibleThumb);
        log('Activated first allowed thumbnail (previous active was removed)');
      }, 50);
    }

    // --- Filter viewer/slide items (REMOVE from DOM) ---
    var hiddenViewerCount = 0;
    if (config.viewerSelector) {
      var viewerItems = document.querySelectorAll(config.viewerSelector);
      viewerItems.forEach(function(item) {
        var alt = getAltText(item);
        if (!alt) {
          item.classList.add('b2b-revealed');
          return;
        }

        var matched = matchAlt(alt);
        if (matched && !matched.allowed) {
          // Track media ID
          var mediaId = item.getAttribute('data-media-id');
          if (!mediaId) {
            var child = item.querySelector('[data-media-id]');
            if (child) mediaId = child.getAttribute('data-media-id');
          }
          if (mediaId) blockedMediaIds.add(mediaId);

          // REMOVE from DOM — fixes zoom/lightbox showing wrong colors
          item.remove();
          hiddenViewerCount++;
          log('REMOVE viewer item - "' + matched.name + '" (' + matched.code + ')');
        } else {
          item.classList.add('b2b-revealed');
        }
      });
    }

    // --- Filter zoom/lightbox images that may already exist ---
    if (blockedMediaIds.size > 0) {
      filterZoomLightbox(blockedMediaIds, allMatchEntries);
    }

    // --- Watch for dynamically created lightbox/zoom elements ---
    if (blockedMediaIds.size > 0) {
      observeLightbox(blockedMediaIds, allMatchEntries);
    }

    // Remove the preload-hide stylesheet
    revealAll();

    // Reinitialize scroll indicators after DOM changes
    if (hiddenThumbCount > 0) {
      setTimeout(reinitScrollIndicators, 150);
    }

    console.log('[B2B-PDP-Filter] Done:', hiddenThumbCount, 'thumbs removed,', hiddenViewerCount, 'viewer items removed, out of', thumbItems.length, 'total thumbnails');
  }

  // ---- Filter existing zoom/lightbox elements ----
  function filterZoomLightbox(blockedMediaIds, matchEntries) {
    // Gallery zoom carousel items (Clean Canvas Enterprise theme)
    var zoomItems = document.querySelectorAll('.gallery-zoom__item, .gallery-zoom__slide, .gallery-zoom .media');
    zoomItems.forEach(function(item) {
      var mediaId = item.getAttribute('data-media-id');
      if (mediaId && blockedMediaIds.has(mediaId)) {
        item.remove();
        log('REMOVE zoom item - media ID:', mediaId);
        return;
      }
      // Fallback: check alt text
      var img = item.querySelector('img');
      if (img && img.alt) {
        var normalizedAlt = normalize(img.alt);
        for (var i = 0; i < matchEntries.length; i++) {
          if (normalizedAlt.indexOf(matchEntries[i].name) > -1 && !matchEntries[i].allowed) {
            item.remove();
            log('REMOVE zoom item by alt - "' + matchEntries[i].name + '"');
            break;
          }
        }
      }
    });
  }

  // ---- Watch for dynamically created lightbox/zoom and filter it ----
  function observeLightbox(blockedMediaIds, matchEntries) {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;

          // Check if a gallery-zoom or lightbox container was added
          var isZoom = node.classList && (
            node.classList.contains('gallery-zoom') ||
            node.classList.contains('pswp') ||
            node.classList.contains('lightbox')
          );

          var zoomContainer = isZoom ? node : node.querySelector && node.querySelector('.gallery-zoom, .pswp, .lightbox');

          if (zoomContainer) {
            log('Lightbox/zoom detected — filtering blocked images...');
            // Delay to let the zoom populate its slides
            setTimeout(function() {
              filterZoomImages(zoomContainer, blockedMediaIds, matchEntries);
            }, 100);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Auto-disconnect after 5 minutes (page likely navigated away)
    setTimeout(function() { observer.disconnect(); }, 5 * 60 * 1000);
  }

  // ---- Filter images inside a zoom/lightbox container ----
  function filterZoomImages(container, blockedMediaIds, matchEntries) {
    // Find all images/slides inside the zoom container
    var items = container.querySelectorAll('[data-media-id], .gallery-zoom__item, .gallery-zoom__slide, .pswp__item, img');

    items.forEach(function(item) {
      // Check by media ID
      var mediaId = item.getAttribute('data-media-id');
      if (mediaId && blockedMediaIds.has(mediaId)) {
        // Remove the slide/container, not just the img
        var slide = item.closest('.gallery-zoom__item, .gallery-zoom__slide, .pswp__item') || item;
        slide.remove();
        log('REMOVE lightbox slide - media ID:', mediaId);
        return;
      }

      // Check by alt text
      var alt = item.alt || (item.querySelector && item.querySelector('img') ? item.querySelector('img').alt : '');
      if (alt) {
        var normalizedAlt = normalize(alt);
        for (var i = 0; i < matchEntries.length; i++) {
          if (normalizedAlt.indexOf(matchEntries[i].name) > -1 && !matchEntries[i].allowed) {
            var slide = item.closest('.gallery-zoom__item, .gallery-zoom__slide, .pswp__item') || item;
            slide.remove();
            log('REMOVE lightbox slide by alt - "' + matchEntries[i].name + '"');
            break;
          }
        }
      }
    });

    // Update lightbox navigation count if available
    var counter = container.querySelector('.gallery-zoom__counter, .pswp__counter');
    if (counter) {
      var remaining = container.querySelectorAll('.gallery-zoom__item, .gallery-zoom__slide, .pswp__item').length;
      log('Lightbox slides remaining after filter:', remaining);
    }
  }

  // ---- Main filter function ----
  function filterPDPMedia(config) {
    if (!isB2BCustomer()) {
      console.log('[B2B-PDP-Filter] Not a B2B customer — skipping');
      revealAll();
      return;
    }

    var mapEl = document.getElementById('product-variant-media-map');
    if (!mapEl) {
      log('No variant media map found');
      revealAll();
      return;
    }

    var productData;
    try {
      productData = JSON.parse(mapEl.textContent);
    } catch(e) {
      console.error('[B2B-PDP-Filter] Failed to parse variant media map:', e);
      revealAll();
      return;
    }

    // --- Step 1: Extract color codes from variant SKUs ---
    var colorData = {};
    (productData.variants || []).forEach(function(v) {
      if (!v.sku) return;
      var code = extractColorCode(v.sku);
      if (!code) return;

      if (!colorData[code]) {
        var chartNames = COLOR_CHART[code] || null;
        if (!chartNames) {
          var COLOR_OPTION_NAMES = ['color', 'couleur', 'colour'];
          var colorIndex = -1;
          (productData.options || []).forEach(function(opt, idx) {
            if (COLOR_OPTION_NAMES.indexOf(opt.toLowerCase()) > -1) colorIndex = idx;
          });
          var optionVal = colorIndex === 0 ? v.option1 : colorIndex === 1 ? v.option2 : colorIndex === 2 ? v.option3 : null;
          if (optionVal) {
            chartNames = [normalize(optionVal)];
            log('Color code', code, 'NOT in chart, using option value fallback:', optionVal);
          } else {
            log('Color code', code, 'NOT in chart and no option fallback, skipping');
            return;
          }
        }
        colorData[code] = { names: chartNames, skus: [] };
      }
      colorData[code].skus.push(v.sku);
    });

    log('Color data from SKUs:', JSON.parse(JSON.stringify(colorData)));

    // --- Step 2: Build media alt-text map (normalized) ---
    var mediaAltMap = {};
    (productData.media || []).forEach(function(m) {
      if (m.alt) mediaAltMap[String(m.id)] = normalize(m.alt);
    });
    log('Media alt map:', mediaAltMap);

    // --- Step 3: Try synchronous cache fast-path first ---
    var cachedSkus = getCachedSkus();
    if (cachedSkus) {
      log('FAST PATH: using cached SKUs synchronously');
      applyFilter(config, colorData, mediaAltMap, cachedSkus);
      return;
    }

    // --- Step 4: Async fallback — fetch from Worker ---
    log('SLOW PATH: fetching from Worker v2 API...');
    fetchAllowedSkusFromWorker().then(function(skus) {
      applyFilter(config, colorData, mediaAltMap, skus);
    });
  }

  // ---- Wait for SparkLayer/B2B data, then filter ----
  function waitAndFilter(config) {
    var maxWait = 500;  // Reduced from 2000ms — Worker v2 is faster
    var checkInterval = 50;
    var waited = 0;

    function check() {
      var hasCachedData = !!sessionStorage.getItem(CACHE_KEY);
      var hasPriceLists = !!(window.__B2B_PRICE_LIST_SLUGS && window.__B2B_PRICE_LIST_SLUGS.length > 0);
      var hasPdpPriceLists = !!(window.__B2B_PDP_PRICE_LIST_SLUGS && window.__B2B_PDP_PRICE_LIST_SLUGS.length > 0);
      var hasCustomerGroup = !!window.__B2B_CUSTOMER_GROUP_SLUG;
      var hasSparkLayer = !!(window.spark && window.spark.ready);

      if (hasCachedData || hasPriceLists || hasPdpPriceLists || hasCustomerGroup || hasSparkLayer || waited >= maxWait) {
        log('Starting filter (cache:', hasCachedData, ', PLs:', hasPriceLists, ', pdpPLs:', hasPdpPriceLists, ', group:', hasCustomerGroup, ', spark:', hasSparkLayer, ')');
        filterPDPMedia(config);
      } else {
        waited += checkInterval;
        setTimeout(check, checkInterval);
      }
    }

    check();
  }

  // ---- Public API ----
  window.B2BPDPFilter = {
    init: function(config) {
      console.log('[B2B-PDP-Filter] Script loaded, v6 DOM-removal');
      // DOM should be ready since this is called from bottom of section.
      // If somehow called early, fall back to DOMContentLoaded.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() { waitAndFilter(config); });
      } else {
        waitAndFilter(config);
      }
    }
  };
})();
